import { isAxiosError, isCancel, type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { env } from 'app/lib/env';
import { storage } from 'app/lib/storage';
import { StorageKey } from 'app/lib/storageKeys';
import { hydrateSessionState, type SessionState } from './session-state';
import { jwtDecode } from 'jwt-decode';

type EndpointStorage = Pick<typeof storage, 'readString' | 'saveString'>;

interface ApiEndpointConfiguration {
  defaultBaseUrl: string;
  defaultRegion?: ApiRegion;
  usaBaseUrl?: string;
}

interface ApiEndpointRoutingOptions {
  routeGuestListingsToCanada?: () => boolean;
}

export type ApiRegion = 'CA' | 'US';
export const API_REGION_MISMATCH_CODE = 'API_REGION_MISMATCH';

const normalizeBaseUrl = (baseUrl: string): string => baseUrl.replace(/\/+$/, '');

export class ApiEndpointManager {
  private readonly baseUrls: string[];
  private readonly baseUrlsByRegion: Partial<Record<ApiRegion, string>>;
  private activeBaseUrl?: string;
  private pendingCountryBaseUrl?: string;
  private persistedBaseUrl?: string | null;
  private hydrationPromise?: Promise<void>;

  public constructor(
    { defaultBaseUrl, defaultRegion = 'CA', usaBaseUrl }: ApiEndpointConfiguration,
    private readonly endpointStorage: EndpointStorage = storage,
  ) {
    const canadaBaseUrl = normalizeBaseUrl(defaultBaseUrl);
    const normalizedUsaBaseUrl = usaBaseUrl ? normalizeBaseUrl(usaBaseUrl) : undefined;
    this.baseUrlsByRegion = {
      CA: canadaBaseUrl,
      US: normalizedUsaBaseUrl,
    };
    const preferredDefaultBaseUrl = this.baseUrlsByRegion[defaultRegion] ?? canadaBaseUrl;
    this.baseUrls = [
      ...new Set(
        [preferredDefaultBaseUrl, canadaBaseUrl, normalizedUsaBaseUrl].filter((baseUrl): baseUrl is string =>
          Boolean(baseUrl),
        ),
      ),
    ];
  }

  public preferForSignupCountry(country: string | null | undefined): void {
    this.preferForRegion(country?.toUpperCase() === 'US' ? 'US' : 'CA');
  }

  public preferForRegion(region: ApiRegion): void {
    this.pendingCountryBaseUrl = this.getBaseUrlForRegion(region);
  }

  public getBaseUrlForRegion(region: ApiRegion): string | undefined {
    return this.baseUrlsByRegion[region];
  }

  public async isRegionPreferred(region: ApiRegion): Promise<boolean> {
    const [preferredBaseUrl] = await this.getPrioritizedBaseUrls();
    return preferredBaseUrl === this.getBaseUrlForRegion(region);
  }

  public async getPrioritizedBaseUrls(): Promise<string[]> {
    await this.hydrate();
    return [this.pendingCountryBaseUrl, this.activeBaseUrl, ...this.baseUrls].filter(
      (baseUrl, index, urls): baseUrl is string => Boolean(baseUrl) && urls.indexOf(baseUrl) === index,
    );
  }

  public isConfigured(baseUrl: string | undefined): baseUrl is string {
    return Boolean(baseUrl && this.baseUrls.includes(normalizeBaseUrl(baseUrl)));
  }

  public async markSuccessful(baseUrl: string): Promise<void> {
    const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
    if (!this.baseUrls.includes(normalizedBaseUrl)) return;

    this.activeBaseUrl = normalizedBaseUrl;
    if (this.pendingCountryBaseUrl === normalizedBaseUrl) this.pendingCountryBaseUrl = undefined;

    if (this.baseUrls.length > 1 && this.persistedBaseUrl !== normalizedBaseUrl) {
      await this.endpointStorage.saveString(StorageKey.PreferredApiBaseUrl, normalizedBaseUrl);
      this.persistedBaseUrl = normalizedBaseUrl;
    }
  }

  private async hydrate(): Promise<void> {
    if (this.persistedBaseUrl !== undefined) return;
    if (this.hydrationPromise) return this.hydrationPromise;

    this.hydrationPromise = this.endpointStorage.readString(StorageKey.PreferredApiBaseUrl).then((storedBaseUrl) => {
      const normalizedBaseUrl = storedBaseUrl ? normalizeBaseUrl(storedBaseUrl) : null;
      this.persistedBaseUrl = normalizedBaseUrl && this.baseUrls.includes(normalizedBaseUrl) ? normalizedBaseUrl : null;
      this.activeBaseUrl = this.persistedBaseUrl ?? undefined;
    });

    try {
      await this.hydrationPromise;
    } finally {
      this.hydrationPromise = undefined;
    }
  }
}

const isAuthenticationPost = (config: InternalAxiosRequestConfig): boolean => {
  const path = (config.url ?? '').split('?')[0]?.replace(/\/+$/, '').toLowerCase();
  return config.method?.toLowerCase() === 'post' && path === '/authentication';
};

const isListingsGet = (config: InternalAxiosRequestConfig): boolean => {
  const path = (config.url ?? '').split('?')[0]?.replace(/\/+$/, '').toLowerCase();
  return config.method?.toLowerCase() === 'get' && Boolean(path === '/listings' || path?.startsWith('/listings/'));
};

const isUnauthenticated = (session: SessionState): boolean => !session.accessToken && !session.currentUserId;

const isApiUnavailable = (error: AxiosError): boolean => {
  if (isCancel(error) || error.code === 'ERR_CANCELED') return false;
  if (!error.response) return true;
  return [502, 503, 504].includes(error.response.status);
};

const getRegionMismatch = (error: AxiosError): ApiRegion | undefined => {
  const data = error.response?.data;
  if (!data || typeof data !== 'object') return;

  const { code, region } = data as { code?: unknown; region?: unknown };
  if (code !== API_REGION_MISMATCH_CODE || (region !== 'CA' && region !== 'US')) return;
  return region;
};

export function installApiEndpointFailover(
  http: AxiosInstance,
  manager: ApiEndpointManager,
  getSession: () => Promise<SessionState> = hydrateSessionState,
  routingOptions: ApiEndpointRoutingOptions = {},
): void {
  http.interceptors.request.use(async (config) => {
    if (config.lpcApiEndpoint) return config;

    const session = await getSession();
    const routeGuestListingsToCanada =
      isUnauthenticated(session) && isListingsGet(config) && routingOptions.routeGuestListingsToCanada?.();
    const [preferredBaseUrl] = await manager.getPrioritizedBaseUrls();
    const requestedBaseUrl = routeGuestListingsToCanada
      ? (manager.getBaseUrlForRegion('CA') ?? preferredBaseUrl)
      : preferredBaseUrl;
    if (requestedBaseUrl) config.baseURL = requestedBaseUrl;

    if (
      isUnauthenticated(session) ||
      isAuthenticationPost(config) ||
      Boolean(session.loginAsEmail || session.loginAsUserId)
    ) {
      config.lpcApiEndpoint = {
        allowFallback: !routeGuestListingsToCanada,
        attemptedBaseUrls: requestedBaseUrl ? [requestedBaseUrl] : [],
        originalData: config.data,
        preservePreferredEndpoint: routeGuestListingsToCanada,
      };
    }

    return config;
  });

  http.interceptors.response.use(
    async (response) => {
      const baseUrl = response.config.baseURL;
      if (manager.isConfigured(baseUrl) && !response.config.lpcApiEndpoint?.preservePreferredEndpoint) {
        await manager.markSuccessful(baseUrl);
        http.defaults.baseURL = normalizeBaseUrl(baseUrl);
      }
      return response;
    },
    async (error: unknown) => {
      if (!isAxiosError(error) || !error.config?.lpcApiEndpoint?.allowFallback) {
        throw error;
      }

      const state = error.config.lpcApiEndpoint;
      const baseUrls = await manager.getPrioritizedBaseUrls();
      const mismatchRegion = getRegionMismatch(error);
      if (!mismatchRegion) {
        if (!isApiUnavailable(error)) throw error;
        const method = error.config.method?.toLowerCase() ?? 'get';
        if (!['get', 'head', 'options'].includes(method) && !isAuthenticationPost(error.config)) throw error;
      }

      const requiredBaseUrl = mismatchRegion ? manager.getBaseUrlForRegion(mismatchRegion) : undefined;
      const nextBaseUrl = requiredBaseUrl ?? baseUrls.find((baseUrl) => !state.attemptedBaseUrls.includes(baseUrl));
      if (nextBaseUrl && state.attemptedBaseUrls.includes(nextBaseUrl)) throw error;
      if (!nextBaseUrl) throw error;

      const retryConfig: InternalAxiosRequestConfig = {
        ...error.config,
        baseURL: nextBaseUrl,
        data: state.originalData,
        lpcApiEndpoint: {
          ...state,
          attemptedBaseUrls: [...state.attemptedBaseUrls, nextBaseUrl],
        },
      };
      return http.request(retryConfig);
    },
  );
}

export const apiEndpointManager = new ApiEndpointManager({
  defaultBaseUrl: env.API_BASE_URL,
  defaultRegion: env.IS_PARTAKE ? 'US' : 'CA',
  usaBaseUrl: env.USA_API_BASE_URL,
});

export function getAuthenticationApiBaseUrl(accessToken: string | null | undefined): string | undefined {
  if (!accessToken) return undefined;
  try {
    const { iss } = jwtDecode<{ iss?: string }>(accessToken);
    const awsRegion = /cognito-idp\.([^.]+)\.amazonaws\.com/.exec(iss ?? '')?.[1];
    if (awsRegion === 'ca-central-1') return apiEndpointManager.getBaseUrlForRegion('CA');
    if (awsRegion === 'us-east-1') return apiEndpointManager.getBaseUrlForRegion('US');
  } catch {
    return undefined;
  }
  return undefined;
}

export function preferApiEndpointForSignupCountry(country: string | null | undefined): void {
  apiEndpointManager.preferForSignupCountry(country);
}
